import React, { useCallback, useEffect, useRef } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import useClickOutside from '@/hooks/useClickOutside';
import ControlPopupPresence from './ControlPopupPresence';
import {
  DEFAULT_PLAYBACK_RATE,
  MAX_PLAYBACK_RATE,
  MIN_PLAYBACK_RATE,
  PLAYBACK_RATE_STEP,
  formatPlaybackRate,
  normalizePlaybackRate,
} from '@/hooks/usePlaybackSpeed';

const PRESET_PLAYBACK_RATES = [0.75, 0.9, 1.0, 1.1, 1.5, 2.0];

type ButtonSize = 'base' | 'sm' | 'md' | 'lg' | 'xl';

interface PlaybackSpeedControlProps {
  /** DOM id of the popup, for aria-controls. */
  id: string;
  playbackRate: number;
  onPlaybackRateChange: (rate: number) => void;
  isOpen: boolean;
  /** Toggle the popup. Parents use this to close their other popups first. */
  onToggle: () => void;
  onClose: () => void;
  size: ButtonSize;
}

const buttonSizes: Record<ButtonSize, string> = {
  base: 'h-12 min-w-12 text-lg',
  sm: 'h-12 min-w-12 text-lg',
  md: 'h-14 min-w-14 text-xl',
  lg: 'h-16 min-w-16 text-xl',
  xl: 'h-18 min-w-18 text-xl',
};

const controlSlotSizes: Record<ButtonSize, string> = {
  base: 'w-16',
  sm: 'w-16',
  md: 'w-16',
  lg: 'w-16',
  xl: 'w-16',
};

/**
 * The speed control that sits next to the sleep timer: a button labelled with
 * the current rate ("1x") which opens a slider from 0.75x to 2x.
 */
const PlaybackSpeedControl: React.FC<PlaybackSpeedControlProps> = ({
  id,
  playbackRate,
  onPlaybackRateChange,
  isOpen,
  onToggle,
  onClose,
  size,
}) => {
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  const containerRef = useRef<HTMLDivElement>(null);
  const sliderRef = useRef<HTMLDivElement>(null);
  const label = formatPlaybackRate(playbackRate);
  const isAdjusted = playbackRate !== DEFAULT_PLAYBACK_RATE;
  const progressPercent =
    ((playbackRate - MIN_PLAYBACK_RATE) / (MAX_PLAYBACK_RATE - MIN_PLAYBACK_RATE)) * 100;

  useClickOutside(containerRef, onClose);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Native `<input type="range">` ties drag-continuation to the browser's own
  // notion of the thumb's hit area, which on mobile browsers is when
  // the touch begins over the (small) visible thumb -- enlarging the
  // thumb's CSS box doesnt fix that. Driving the drag ourselves via
  // Pointer Events, like the audio progress bar does, means any press
  // anywhere on the slider grabs it and keeps tracking the pointer for the
  // whole gesture, regardless of where it started.
  const rateFromClientX = useCallback((clientX: number) => {
    const track = sliderRef.current;
    if (!track) return MIN_PLAYBACK_RATE;
    const rect = track.getBoundingClientRect();
    const ratio = rect.width > 0 ? (clientX - rect.left) / rect.width : 0;
    const clamped = Math.max(0, Math.min(1, ratio));
    return normalizePlaybackRate(MIN_PLAYBACK_RATE + clamped * (MAX_PLAYBACK_RATE - MIN_PLAYBACK_RATE));
  }, []);

  const handleSliderPointerDown = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    onPlaybackRateChange(rateFromClientX(event.clientX));
  }, [onPlaybackRateChange, rateFromClientX]);

  const handleSliderPointerMove = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (!event.currentTarget.hasPointerCapture(event.pointerId)) return;
    onPlaybackRateChange(rateFromClientX(event.clientX));
  }, [onPlaybackRateChange, rateFromClientX]);

  const handleSliderPointerUp = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handleSliderKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    switch (event.key) {
      case 'ArrowLeft':
      case 'ArrowDown':
        event.preventDefault();
        onPlaybackRateChange(normalizePlaybackRate(playbackRate - PLAYBACK_RATE_STEP));
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        event.preventDefault();
        onPlaybackRateChange(normalizePlaybackRate(playbackRate + PLAYBACK_RATE_STEP));
        break;
      case 'Home':
        event.preventDefault();
        onPlaybackRateChange(MIN_PLAYBACK_RATE);
        break;
      case 'End':
        event.preventDefault();
        onPlaybackRateChange(MAX_PLAYBACK_RATE);
        break;
      default:
        break;
    }
  }, [onPlaybackRateChange, playbackRate]);

  return (
    <div ref={containerRef} className={`relative shrink-0 ${controlSlotSizes[size]}`}>
      <button
        type="button"
        aria-label={`Playback speed: ${label}`}
        aria-expanded={isOpen}
        aria-controls={id}
        aria-pressed={isAdjusted}
        onClick={onToggle}
        className={`mx-auto flex cursor-pointer items-center justify-center rounded-full px-2 font-medium tracking-tight transition-colors ${
          buttonSizes[size]
        } ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}
      >
        {label}
      </button>

      <ControlPopupPresence isOpen={isOpen}>
        {(animationState) => (
          <div
            id={id}
            className={`absolute right-0 bottom-full z-50 mb-2 w-80 max-w-[calc(100vw-2rem)] rounded-lg border p-2 shadow-2xl md:right-auto md:left-1/2 md:-translate-x-1/2 control-popup-native-${animationState} ${
              isDarkMode ? 'border-zinc-700 bg-zinc-800 text-gray-200' : 'border-gray-300 bg-white text-gray-700'
            }`}
            style={{
              transformOrigin: 'bottom center',
              touchAction: 'pan-y',
              willChange: 'transform, opacity',
            }}
            aria-label="Reading speed"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-center justify-between px-3 pt-2.5 pb-2">
              <h2 className="text-sm font-semibold">Reading speed</h2>
              <span className="text-sm font-semibold tabular-nums" role="status">
                {label}
              </span>
            </div>

            <div className="px-3 pb-2">
              <div
                ref={sliderRef}
                role="slider"
                tabIndex={0}
                aria-label="Reading speed"
                aria-valuemin={MIN_PLAYBACK_RATE}
                aria-valuemax={MAX_PLAYBACK_RATE}
                aria-valuenow={playbackRate}
                aria-valuetext={label}
                onPointerDown={handleSliderPointerDown}
                onPointerMove={handleSliderPointerMove}
                onPointerUp={handleSliderPointerUp}
                onPointerCancel={handleSliderPointerUp}
                onKeyDown={handleSliderKeyDown}
                className={`playback-speed-slider ${isDarkMode ? 'playback-speed-slider--dark' : 'playback-speed-slider--light'}`}
              >
                <div className="playback-speed-slider__track">
                  <div className="playback-speed-slider__fill" style={{ width: `${progressPercent}%` }} />
                </div>
                <div className="playback-speed-slider__thumb" style={{ left: `${progressPercent}%` }} />
              </div>
              <div className="flex justify-between gap-1 pt-2">
                {PRESET_PLAYBACK_RATES.map((rate) => {
                  const isActive = normalizePlaybackRate(rate) === normalizePlaybackRate(playbackRate);
                  return (
                    <button
                      key={rate}
                      type="button"
                      onClick={() => onPlaybackRateChange(rate)}
                      aria-pressed={isActive}
                      className={`rounded-md px-1.5 py-1 text-xs font-medium tabular-nums transition-colors ${
                        isActive
                          ? isDarkMode
                            ? 'bg-gray-200 text-zinc-900'
                            : 'bg-gray-800 text-white'
                          : isDarkMode
                            ? 'text-gray-400 hover:bg-zinc-700'
                            : 'text-gray-500 hover:bg-gray-100'
                      }`}
                    >
                      {formatPlaybackRate(rate)}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </ControlPopupPresence>
    </div>
  );
};

export default PlaybackSpeedControl;
